/*
 * sessionCache — pre-generated question cache so mode entry has no lag.
 *
 * Seeded at Session Settings "Start" from the materialized session plan
 * (config.plan.by_mode). Modes `take()` items instantly; when a queue runs low,
 * `refill()` tops it up in the background. Mirrored to sessionStorage keyed by
 * the concept scope, so a reload / re-entry reuses the same batch.
 */
import APIService from '../../services/APIService';

const mem = {};        // scopeKey -> { mode -> [items] }
const refilling = {};  // "scope:mode" -> bool
const STORE_KEY = 'nano_qcache_v1';

const scopeKey = (conceptIds) => (conceptIds || []).slice().sort().join('|');

const loadStore = () => { try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}'); } catch { return {}; } };
const saveStore = (s) => { try { sessionStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch { /* quota */ } };

function bucket(sk) {
  if (!mem[sk]) mem[sk] = loadStore()[sk] || {};
  return mem[sk];
}
function persist(sk) {
  const store = loadStore();
  store[sk] = mem[sk];
  saveStore(store);
}

/** Seed queues from the materialized plan's by_mode (only fills empty queues). */
export function seed(conceptIds, byMode) {
  if (!byMode) return;
  const sk = scopeKey(conceptIds);
  const b = bucket(sk);
  Object.entries(byMode).forEach(([mode, items]) => {
    if (Array.isArray(items) && items.length && !(b[mode] && b[mode].length)) {
      b[mode] = items.slice();
    }
  });
  persist(sk);
}

export function count(conceptIds, mode) {
  return (bucket(scopeKey(conceptIds))[mode] || []).length;
}

/** Pop the next cached item (undefined if the queue is empty). */
export function take(conceptIds, mode) {
  const sk = scopeKey(conceptIds);
  const b = bucket(sk);
  const q = b[mode] || [];
  const item = q.shift();
  b[mode] = q;
  persist(sk);
  return item;
}

/** A copy of the whole queue WITHOUT removing it (deck/mix modes reuse on re-entry). */
export function peekAll(conceptIds, mode) {
  return (bucket(scopeKey(conceptIds))[mode] || []).slice();
}

/** Drain the whole queue (for deck/mix modes that want everything at once). */
export function takeAll(conceptIds, mode) {
  const sk = scopeKey(conceptIds);
  const b = bucket(sk);
  const q = b[mode] || [];
  b[mode] = [];
  persist(sk);
  return q;
}

async function generate(mode, conceptIds) {
  if (mode === 'mcq') return [await APIService.engineMcq({ conceptIds })];
  if (mode === 'theory') { const q = await APIService.engineTheoryNext({ conceptIds }); return (q && q.question) ? [q] : []; }
  if (mode === 'flashcards') return (await APIService.engineFlashcards({ conceptIds, count: 8 })).cards || [];
  return [];
}

/** Drop every queue for ONE scope — a revised plan makes its content stale. */
export function clearScope(conceptIds) {
  const sk = scopeKey(conceptIds);
  delete mem[sk];
  const store = loadStore();
  delete store[sk];
  saveStore(store);
}

/** Wipe everything — a NEW session must not replay the old session's items. */
export function clear() {
  Object.keys(mem).forEach((k) => { delete mem[k]; });
  try { sessionStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
}

/** Background top-up when a queue drops below `min`. Idempotent per scope+mode. */
export function refill(conceptIds, mode, min = 2) {
  if (!(conceptIds && conceptIds.length)) return;
  const sk = scopeKey(conceptIds);
  const rk = `${sk}:${mode}`;
  if (refilling[rk] || count(conceptIds, mode) >= min) return;
  refilling[rk] = true;
  generate(mode, conceptIds)
    .then((items) => { const b = bucket(sk); b[mode] = (b[mode] || []).concat(items); persist(sk); })
    .catch(() => {})
    .finally(() => { refilling[rk] = false; });
}
